import { useState, useEffect } from "react";
import { Row, Col, OverlayTrigger, Popover } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import StatsOverview from "../../backend/components/Posts/StatsOverview"; // adjust path
import PlatformIcons, { scheduledPlatformsOf, missedSchedulesOf, platformsOfSchedule } from "../PlatformIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faHourglassHalf, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import UserAvatar from "../UserAvatar";
import GroupLogo from "../GroupLogo";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import { funnelStageLabel } from "../../Form/FunnelStageSelect";

/**
 * `author`  {id, name}: who wrote the post; always shown as avatar + name.
 *           id "me" loads the current user's own picture. No name -> "- -".
 * `currentUsername`: lets the approval indicator say "your approval".
 * `readOnly`: the viewer may not edit; dashed edge and muted ground.
 */
const GenericPostItem = ({ post, task, provided, ItemMenu, onEdit, schedule = null, cardStyle = {}, titlePrefix = null, readOnly = false, author = null, currentUsername = null }) => {
  const { t } = useTranslation();
  const [statusBarColor, setStatusBarColor] = useState("#038edc");

  // Schedules that passed without anything being posted: the card turns red.
  const missed = missedSchedulesOf(post);
  // Delegated schedules still waiting for the publishing user's decision.
  const proposed = (post?.schedules ?? []).filter((s) => s?.publish_as && s.approval_status === "proposed");
  const authorName = (author?.name && String(author.name).trim()) || "- -";
  // Groups the post is shared in (from the listing); their logos sit in the header.
  const sharedIn = Array.isArray(post?.shared_in) ? post.shared_in : [];
  const groupInitials = (name) => {
    const words = String(name ?? "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    return words.length > 1 ? words.slice(0, 2).map((w) => w[0].toUpperCase()).join("") : words[0].slice(0, 2).toUpperCase();
  };
  // Initials fall back to the e-mail when no name is set; the visible label never does.
  const initialsOf = (p) => (p?.name && String(p.name).trim()) || p?.username || "?";
  // Who the post is planned for (distinct, rejected/cancelled left out): shown
  // as "author -> target avatar(s)" in the header.
  const plannedFor = (() => {
    const seen = new Map();
    for (const sch of post?.schedules ?? []) {
      const target = sch?.publish_as;
      if (!target || ["rejected", "cancelled"].includes(sch.approval_status)) continue;
      const key = target.id ?? target.username;
      if (!seen.has(key)) seen.set(key, { ...target, status: sch.approval_status, when: sch.local_user_time || sch.scheduled_for });
    }
    return [...seen.values()];
  })();

  useEffect(() => {
    // optional: green if published, red if a schedule was missed
    const published =
      post?.status === "published" ||
      post?.schedules?.some((s) => s.post_status === "published");
    if (missed.length > 0 && !published) setStatusBarColor("#f34e4e");
    else if (published) setStatusBarColor("#51d28c");
    else setStatusBarColor("#038edc");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  // Platforms this post is scheduled for (from its schedules); empty when unscheduled.
  const scheduledPlatforms = scheduledPlatformsOf(post);
  const pillarName = post?.content_pillar?.pillar ?? null;
  const tags = post?.tags ?? [];
  const postType = t(post?.post_type ? `Backend.Post.PostType.${post.post_type}` : "Backend.Post.PostType.Text");
  const contentType = t(post?.content_type ? `Backend.Post.ContentType.${post.content_type}` : "Backend.Post.ContentType.Personal");
  const funnelStage = funnelStageLabel(post?.funnel_stage, t);

  return (
    <div
      ref={provided?.innerRef}
      {...(provided?.draggableProps || {})}
      {...(provided?.dragHandleProps || {})}
      className="card mb-2"
      style={{
        // Read-only cards (someone else's post on my account) get a dashed edge
        // and a muted ground, the common "not yours to move" cue.
        borderLeft: readOnly ? `2px dashed ${statusBarColor}` : `2px solid ${statusBarColor}`,
        background: readOnly ? "#f8f9fb" : undefined,
        cursor: onEdit ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        ...(provided?.draggableProps?.style || {}),
        ...cardStyle,
      }}
      onClick={onEdit}
    >
      <div className="card-header" style={{ paddingTop: 8, paddingBottom: 8, display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Row 1: who wrote it (always), the menu top-right. The title lives in the body. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {titlePrefix}
            <UserAvatar userId={author?.id} name={initialsOf(author)} size={24} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1a2236", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={authorName}>
              {authorName}
            </span>
            {plannedFor.length > 0 && (
              <>
                <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 11, color: "#9299a8", flexShrink: 0 }} />
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  {plannedFor.map((target, i) => {
                    const targetName = (target.name && String(target.name).trim()) || "- -";
                    const stateLabel = target.status === "proposed"
                      ? t("Backend.Post.State.WaitingApproval", "waiting for approval")
                      : target.status === "approved"
                      ? t("Backend.Post.State.Approved", "approved")
                      : "";
                    return (
                      <span
                        key={target.id ?? target.username ?? i}
                        title={`${t("Backend.Post.Schedule.PlannedFor", "Planned for")} ${targetName}${stateLabel ? ` · ${stateLabel}` : ""}`}
                        style={{ marginLeft: i === 0 ? 0 : -6, borderRadius: "50%", boxShadow: "0 0 0 2px #fff", display: "inline-flex" }}
                      >
                        <UserAvatar userId={target.id} name={initialsOf(target)} size={22} />
                      </span>
                    );
                  })}
                </span>
              </>
            )}
            {schedule ? (
              <small className="text-muted">
                {/* keep schedule rendering outside or pass a ready-to-render string */}
                {schedule}
              </small>
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {sharedIn.map((g) => (
              <GroupLogo
                key={g.id}
                groupId={g.id}
                size={22}
                style={{ borderRadius: 6, background: "rgba(86,74,177,0.12)", color: "#564ab1", fontSize: 10, fontWeight: 800 }}
                fallback={groupInitials(g.name)}
                className=""
                title={g.name}
              />
            ))}
            {ItemMenu ? (
              <div onClick={(e) => e.stopPropagation()} style={{ alignSelf: "flex-start" }}>
                <ItemMenu post={post} task={task} />
              </div>
            ) : null}
          </div>
        </div>

        {/* Row 2: status icons. Platforms open the plan, the hourglass lists pending
            approvals, the warning lists missed schedules. Hidden when there is nothing. */}
        {(scheduledPlatforms.length > 0 || proposed.length > 0 || missed.length > 0) && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 22 }}>
            {scheduledPlatforms.length > 0 && (
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom-start"
                overlay={
                  <Popover id={`plan-${post?.id}`} style={{ maxWidth: 340 }}>
                    <Popover.Header as="h6">{t("Backend.Post.Schedule.PlanTitle", "Plan")}</Popover.Header>
                    <Popover.Body style={{ fontSize: 13, padding: "8px 12px" }}>
                      {(post?.schedules ?? []).map((sch) => {
                        const when = sch.local_user_time || sch.scheduled_for;
                        const isMissed = missed.some((m) => m.id === sch.id);
                        const state = sch.posted_at || sch.post_status === "published"
                          ? { label: t("Backend.Post.State.Published", "Published"), color: "#1a7a4a", bg: "rgba(81,210,140,0.15)" }
                          : isMissed || sch.post_status === "error"
                          ? { label: t("Backend.Post.State.MissedSchedule", "Not published"), color: "#c0392b", bg: "rgba(243,78,78,0.12)" }
                          : sch.publish_as && sch.approval_status === "proposed"
                          ? { label: t("Backend.Post.State.WaitingApproval", "waiting for approval"), color: "#8a5a00", bg: "rgba(247,204,83,0.18)" }
                          : { label: t("Backend.Post.State.Scheduled", "Scheduled"), color: "#038edc", bg: "rgba(3,142,220,0.10)" };
                        return (
                          <div key={sch.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #eef0f3" }}>
                            <PlatformIcons platforms={platformsOfSchedule(sch)} size={15} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: "#1a2236", whiteSpace: "nowrap" }}>
                                {formatDate(when)} <span style={{ color: "#74788d", fontWeight: 400, fontSize: 11 }}>{sch.timezone}</span>
                              </div>
                              {sch.publish_as && (
                                <div style={{ fontSize: 11, color: "#74788d" }}>
                                  {t("Backend.Post.Schedule.OnAccountOf", "on account of")} {(sch.publish_as.name && sch.publish_as.name.trim()) || "- -"}
                                </div>
                              )}
                              {(isMissed || sch.post_status === "error") && sch.post_error && (
                                <div style={{ fontSize: 11, color: "#c0392b" }}>{sch.post_error}</div>
                              )}
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: state.bg, color: state.color, whiteSpace: "nowrap" }}>
                              {state.label}
                            </span>
                          </div>
                        );
                      })}
                    </Popover.Body>
                  </Popover>
                }
              >
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  title={`${t("Backend.Post.Schedule.ScheduledOn", "Scheduled on")}: ${scheduledPlatforms.join(", ")}`}
                  aria-label={t("Backend.Post.Schedule.PlanTitle", "Plan")}
                  style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <PlatformIcons platforms={scheduledPlatforms} title="" />
                </button>
              </OverlayTrigger>
            )}

            {proposed.length > 0 && (
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom-start"
                overlay={
                  <Popover id={`approval-${post?.id}`} style={{ maxWidth: 320 }}>
                    <Popover.Header as="h6" style={{ color: "#8a5a00", display: "flex", alignItems: "center", gap: 6 }}>
                      <FontAwesomeIcon icon={faHourglassHalf} />
                      {t("Backend.Post.State.WaitingApprovalTitle", "Waiting for approval")}
                    </Popover.Header>
                    <Popover.Body style={{ fontSize: 13 }}>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {proposed.map((sch) => {
                          const mine = currentUsername && sch.publish_as?.username === currentUsername;
                          const who = (sch.publish_as?.name && sch.publish_as.name.trim()) || "- -";
                          return (
                            <li key={sch.id}>
                              {formatDate(sch.local_user_time || sch.scheduled_for)}
                              <div style={{ color: "#6b7280", fontSize: 12 }}>
                                {mine
                                  ? t("Backend.Post.State.WaitingYourApproval", "waiting for your approval")
                                  : t("Backend.Post.State.WaitingApprovalBy", "waiting for approval by {{who}}", { who })}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </Popover.Body>
                  </Popover>
                }
              >
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  title={t("Backend.Post.State.WaitingApprovalTitle", "Waiting for approval")}
                  aria-label={t("Backend.Post.State.WaitingApprovalTitle", "Waiting for approval")}
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 22, height: 22, borderRadius: "50%", border: "none", cursor: "pointer",
                    background: "rgba(247,204,83,0.22)", color: "#8a5a00", fontSize: 12, padding: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faHourglassHalf} />
                </button>
              </OverlayTrigger>
            )}

            {missed.length > 0 && (
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom-start"
                overlay={
                  <Popover id={`missed-${post?.id}`} style={{ maxWidth: 320 }}>
                    <Popover.Header as="h6" style={{ color: "#c0392b", display: "flex", alignItems: "center", gap: 6 }}>
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      {t("Backend.Post.State.MissedSchedule", "Not published")}
                    </Popover.Header>
                    <Popover.Body style={{ fontSize: 13 }}>
                      <p style={{ marginBottom: 8 }}>
                        {t("Backend.Post.State.MissedScheduleHint", "The scheduled time passed but nothing was posted.")}
                      </p>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {missed.map((m) => (
                          <li key={m.id}>
                            {formatDate(m.scheduled_for)} {m.timezone ? `(${m.timezone})` : ""}
                            {m.post_error ? <div style={{ color: "#6b7280", fontSize: 12 }}>{m.post_error}</div> : null}
                          </li>
                        ))}
                      </ul>
                    </Popover.Body>
                  </Popover>
                }
              >
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  title={t("Backend.Post.State.MissedSchedule", "Not published")}
                  aria-label={t("Backend.Post.State.MissedSchedule", "Not published")}
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 22, height: 22, borderRadius: "50%", border: "none", cursor: "pointer",
                    background: "rgba(243,78,78,0.12)", color: "#c0392b", fontSize: 12, padding: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </button>
              </OverlayTrigger>
            )}
          </div>
        )}
      </div>

      <div className="card-body" style={{ flex: 1, overflow: "hidden" }}>
        <Row>
          <Col>
            {/* Title sits directly above the text; room on the right for the corner menu. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, marginBottom: 6 }}>
              <h5
                className="font-size-14"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                  color: "#1a2236",
                  margin: 0,
                  minWidth: 0,
                }}
                title={post?.title}
              >
                {post?.title}
              </h5>
            </div>
            <div className="text-muted">
              <p className="mb-0" style={{ whiteSpace: "pre-line" }}>
                {post?.text && (post.text.length > 200 ? post.text.slice(0, 200) + "..." : post.text)}
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="card-footer">
        <StatsOverview post={post} />
        <span className="badge me-1 font-size-12 badge bg-link rounded-pill badge-info-subtle">
          {contentType}
        </span>
        <span className="badge me-1 font-size-12 badge bg-link rounded-pill badge-success-subtle">
          {postType}
        </span>
        {funnelStage && (
          <span className="badge me-1 font-size-12 badge bg-link rounded-pill badge-primary-subtle" title={t("Backend.Post.FunnelStage.Title", "Funnel stage")}>
            {funnelStage}
          </span>
        )}
        {pillarName && (
          <span className="badge me-1 font-size-12 badge bg-link rounded-pill badge-warning-subtle">
            {pillarName}
          </span>
        )}
        {tags.map((tag) => (
          <span key={tag.name} className="badge me-1 font-size-12 badge bg-link rounded-pill badge-secondary-subtle">
            {tag.name}
          </span>
        ))}
      </div>
    </div>
  );
};

export default GenericPostItem;
